// Channel-type compatibility — the CLIENT MIRROR of the gateway's route validator (SR10).
//
// A mirror, not an approximation. The editor blocks a chain before save so the user is not made to
// round-trip a 422, but if the two checks disagree the editor is worse than useless: it either
// refuses something the server would accept, or lets through something the server will reject with
// a different reason. So this file reproduces `mcpgraph_gateway/streams/route_validator.py`
// case by case — the same three refusals, the same wording — and `routes.test.ts` pins the strings
// against the ones the server test asserts.
//
// The two rules SR10 introduced:
//   * an UNDECLARED channel is a refusal, not a blank cheque. Treating silence as "compatible with
//     everything" is what switched ADR-021 off on the exact chain it was written for.
//   * media-type PARAMETERS are compared: `audio/pcm;rate=16000` and `audio/pcm;rate=24000` are
//     the same media type and are not interchangeable. A parameter the downstream does not mention
//     is not constrained by it.

import type { CatalogTool } from '../api/schemas';

/** The explicit "I accept anything" declaration — a claim someone made, not a silence. */
export const ANY_TYPE = '*/*';

export type ChannelProblem =
  | { reason: 'undeclared_channels'; side: 'from' | 'to' }
  | { reason: 'incompatible_channels'; media_type_from: string; media_type_to: string }
  | { reason: 'missing_parameter'; parameter: string; expected: string }
  | { reason: 'parameter_mismatch'; parameter: string; expected: string; actual: string };

/** Split `audio/pcm;rate=16000` into its base type and parameters (keys lower-cased). */
export function parseMediaType(value: string | undefined): {
  base: string;
  params: Record<string, string>;
} {
  const raw = (value ?? '').trim();
  if (!raw) return { base: '', params: {} };
  const [head, ...rest] = raw.split(';');
  const params: Record<string, string> = {};
  for (const part of rest) {
    const at = part.indexOf('=');
    if (at < 0) continue;
    params[part.slice(0, at).trim().toLowerCase()] = part
      .slice(at + 1)
      .trim()
      .replace(/^"|"$/g, '');
  }
  return { base: head.trim().toLowerCase(), params };
}

/** Why DOWN type `a` cannot feed UP type `b` — or `null` if it can. */
export function typeMismatch(
  a: string | undefined,
  b: string | undefined,
): ChannelProblem | null {
  if (a === undefined || a === null) return { reason: 'undeclared_channels', side: 'from' };
  if (b === undefined || b === null) return { reason: 'undeclared_channels', side: 'to' };
  if (a.trim() === ANY_TYPE || b.trim() === ANY_TYPE) return null;
  const from = parseMediaType(a);
  const to = parseMediaType(b);
  if (from.base !== to.base) {
    return {
      reason: 'incompatible_channels',
      media_type_from: from.base,
      media_type_to: to.base,
    };
  }
  for (const [key, want] of Object.entries(to.params)) {
    const got = from.params[key];
    if (got === undefined) {
      return { reason: 'missing_parameter', parameter: key, expected: want };
    }
    if (got !== want) {
      return { reason: 'parameter_mismatch', parameter: key, expected: want, actual: got };
    }
  }
  return null;
}

export interface HopSide {
  index: number;
  selector: string;
  outType?: string;
  inType?: string;
}

/**
 * The sentence a human reads — byte-identical to the server's, because a client that explains a
 * refusal differently from the server is a second source of truth about the same fact.
 */
export function channelErrorMessage(a: HopSide, b: HopSide, problem: ChannelProblem): string {
  if (problem.reason === 'undeclared_channels') {
    const hop = problem.side === 'from' ? a : b;
    const which = problem.side === 'from' ? 'output' : 'input';
    return (
      `hop ${hop.index} (${hop.selector}) declares no ${which} channel type, so the chain ` +
      `cannot be checked; declare channels[] on the tool, or '${ANY_TYPE}' if it really ` +
      `accepts anything`
    );
  }
  if (problem.reason === 'parameter_mismatch') {
    return (
      `hop ${a.index} (${a.selector}) outputs '${a.outType}' but hop ${b.index} ` +
      `(${b.selector}) expects '${b.inType}': ${problem.parameter}=${problem.actual} vs ` +
      `${problem.parameter}=${problem.expected} — insert a converter hop`
    );
  }
  if (problem.reason === 'missing_parameter') {
    return (
      `hop ${a.index} (${a.selector}) outputs '${a.outType}' but hop ${b.index} ` +
      `(${b.selector}) requires ${problem.parameter}=${problem.expected}`
    );
  }
  return (
    `incompatible channel types: hop ${a.index} (${a.selector}) out '${a.outType}' -> ` +
    `hop ${b.index} (${b.selector}) in '${b.inType}'`
  );
}

/** The media-type of a tool's first channel in `dir` (the server's `_channel_type`). */
export function channelType(
  t: CatalogTool | undefined,
  dir: 'UP' | 'DOWN',
): string | undefined {
  if (!t) return undefined;
  for (const c of t.channels) if (c.direction === dir && c.media_type) return c.media_type;
  // Degenerate one-way producer: the tool's top-level media_type is its DOWN type.
  return dir === 'DOWN' ? (t.media_type ?? undefined) : undefined;
}
